import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Flex, Text } from '@atlaskit/primitives/compiled';

import { useUpdateAudioPreferences } from '../../../../common/hooks/useUpdateAudioPreferences';
import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';

import { AudioControlTriggerButton } from './sub-components/AudioControlButton';

const i18n = defineMessages({
	playbackSpeedLabel: {
		id: 'audio.miniplayer-playback-speed-control.playback-speed-label',
		defaultMessage: 'Playback Speed',
		description:
			'The label is used as a tooltip and for accessibility for the select dropdown which provides playback speed options for the actively playing audio',
	},
	speedLabel08x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-08x',
		defaultMessage: '0.8x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 0.8x',
	},
	speedLabel10x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-10x',
		defaultMessage: '1x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.0x',
	},
	speedLabel12x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-12x',
		defaultMessage: '1.2x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.2x',
	},
	speedLabel15x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-15x',
		defaultMessage: '1.5x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.5x',
	},
	speedLabel18x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-18x',
		defaultMessage: '1.8x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.8x',
	},
	speedLabel20x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-20x',
		defaultMessage: '2x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 2.0x',
	},
});


export const PlaybackSpeedControl = (): React.ReactNode => {
	const intl = useIntl();
	const { getCustomization, setPlaybackSpeed } = useAudioPlayerStore();
	const { updateAudioPreferences } = useUpdateAudioPreferences();
	const { audioRef } = useAudioPlayerStreamingContext();

	const PLAYBACK_RATES = useMemo(
		() =>
			new Map([
				[0.8, intl.formatMessage(i18n.speedLabel08x)],
				[1.0, intl.formatMessage(i18n.speedLabel10x)],
				[1.2, intl.formatMessage(i18n.speedLabel12x)],
				[1.5, intl.formatMessage(i18n.speedLabel15x)],
				[1.8, intl.formatMessage(i18n.speedLabel18x)],
				[2.0, intl.formatMessage(i18n.speedLabel20x)],
			]),
		[intl],
	);

	const handleClick = useCallback(
		(speed: number) => {
			if (audioRef.current) {
				try {
					audioRef.current.playbackRate = speed;
				} catch (error) {
					// Some browsers or conditions may make playbackRate read-only
					// We still update the UI and preferences even if setting the audio element fails
				}
			}

			setPlaybackSpeed(speed);

			updateAudioPreferences({
				...getCustomization(),
				playbackSpeed: speed,
			});
		},
		[audioRef, getCustomization, setPlaybackSpeed, updateAudioPreferences],
	);

	return (
		<DropdownMenu
			placement="top-start"
			trigger={({ triggerRef, ...triggerProps }) => (
				<Flex justifyContent="center">
					<AudioControlTriggerButton triggerProps={triggerProps} triggerRef={triggerRef}>
						<Text color={'color.text.subtle'} weight="medium">
							{PLAYBACK_RATES.get(getCustomization().playbackSpeed)}
						</Text>
					</AudioControlTriggerButton>
				</Flex>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{Array.from(PLAYBACK_RATES.entries()).map(([value, label]) => (
					<DropdownItem key={value} onClick={() => handleClick(value)}>
						{label}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
